import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toastr } from 'react-redux-toastr';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';
import { humanize, localTimeString, getRelativeDuration } from 'common/helpers';
import { MACHINE_STATE, MACHINE_CONDITION } from 'common/constants';
import { scrollbarRounded } from 'common/styles/scrollbar';
import * as taskActions from 'home/HomePage/actions/tasks.actions';
import { translateSharedAssetFilterValue } from '../utils/assetListFilterLabels';
import { getUrgencyBucket, ACTIONABLE_REASONS } from './AttentionCard';
import ContextualTrend from './ContextualTrend';

const CRITICAL = '#d92b2b';
const WARNING = '#e8890c';
const INK = '#383838';
const INK_2 = '#6b6e66';
const INK_3 = '#999b95';
const LINE = '#e5e8e1';
const LINE_2 = '#eff0ee';
const BG = '#f6f7f4';
const MUTED_BAR = '#b9bdb3';
// Workflow (not severity) accent for the "new to your queue" score component.
const NEW_SEGMENT = '#3b7dd8';

const CONDITION_META = MACHINE_CONDITION.reduce((acc, opt) => {
  acc[opt.value] = opt;
  return acc;
}, {});

// The urgency score and its per-component weights are owned by the backend and
// arrive on each item as `score_breakdown` ([{ key, points }]). We render that
// verbatim rather than re-deriving weights here, so this panel can never drift
// from the score in the header. `unreviewed` is the backend's key for the
// not-yet-reviewed bonus (it is a workflow factor, not a reason).
const UNREVIEWED_KEY = 'unreviewed';
const REASON_ORDER = ['alarms', 'diagnostics', 'diagnostics_not_evaluable', 'health_score', 'baselines_status', 'missing_asset_info', 'tasks'];

// Severity colours only for severity reasons; chores neutral
const REASON_META = {
  alarms: { glyph: '⚠', titleKey: 'assets.triage.reasons.openAlarm', titleDefault: 'Open Alarm', tone: 'critical', segment: CRITICAL },
  diagnostics: { glyph: '⬡', titleKey: 'assets.triage.reasons.diagnosticRule', titleDefault: 'Diagnostic', tone: 'warning', segment: WARNING },
  diagnostics_not_evaluable: { glyph: '!', titleKey: 'assets.triage.reasons.diagnosticsNotEvaluable', titleDefault: "Diagnostics can't run", tone: 'muted', segment: MUTED_BAR },
  health_score: { glyph: '♥', titleKey: 'assets.triage.reasons.healthScore', titleDefault: 'Health Score', tone: 'warning', segment: WARNING },
  baselines_status: { glyph: '◳', titleKey: 'assets.triage.reasons.baselinesNotConfigured', titleDefault: 'Baselines not configured', tone: 'muted', segment: MUTED_BAR },
  missing_asset_info: { glyph: '◷', titleKey: 'assets.triage.reasons.missingAssetInfo', titleDefault: 'Missing asset info', tone: 'muted', segment: MUTED_BAR },
  tasks: { glyph: '✓', titleKey: 'assets.triage.reasons.openTask', titleDefault: 'Open task', tone: 'muted', segment: MUTED_BAR }
};

const getReasonValues = (item, key) => {
  const reason = item.reason_for_attention || {};
  return reason[key] || [];
};

const getReasonMetadata = (item, key) => {
  const metadata = item.ara_metadata || {};
  return metadata[key] || [];
};

const getTaskId = (value) => {
  if (value && typeof value === 'object') {
    return value.id || value.task_id || value.source_id || value.value;
  }
  return value;
};

// Only a genuine task id can open the task modal / /tasks/:id route. Unlike
// getTaskId (used for labels/keys), this deliberately ignores source_id/value on
// object payloads so a non-task id is never handed to the task-details endpoint.
const getTaskClickId = (value) => {
  if (value && typeof value === 'object') return value.id || value.task_id || null;
  return value;
};

const formatTaskLabel = (value, meta) => {
  if (meta && meta.name) return meta.name;
  if (value && typeof value === 'object' && value.name) return value.name;
  return humanize(String(getTaskId(value) || ''));
};

const SCORE_TONES = {
  urgent: { bg: '#fcefef', fg: CRITICAL },
  elevated: { bg: '#fdf4e7', fg: WARNING },
  routine: { bg: LINE_2, fg: INK_2 }
};

const Pane = styled.div`
  width: 420px;
  min-width: 360px;
  flex-shrink: 0;
  background: white;
  border: 1px solid ${LINE};
  border-radius: 10px;
  margin: 8px 20px 12px 6px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  align-self: flex-start;
  min-height: 0;
  max-height: 100%;
`;

const Header = styled.div`
  flex-shrink: 0;
  padding: 14px 16px 12px;
  border-bottom: 1px solid ${LINE_2};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 3px;
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${props => props.color || '#c9ccc4'};
`;

const Name = styled.h3`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: ${INK};
`;

const ScoreChip = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  background: ${props => props.bg};
  color: ${props => props.fg};
`;

const CloseBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid ${LINE};
  background: white;
  color: ${INK_3};
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;

  &:hover { border-color: ${INK_3}; color: ${INK}; }
`;

const Area = styled.div`
  font-size: 11px;
  color: ${INK_3};
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 13px 16px;
  ${scrollbarRounded}
`;

const Section = styled.div`
  margin-bottom: 16px;
`;

const SectionTitle = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${INK_3};
  margin-bottom: 8px;
`;

const BreakBar = styled.div`
  display: flex;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  background: ${LINE_2};
  margin-bottom: 7px;

  i { height: 100%; }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px 14px;
  font-size: 11px;
  color: ${INK_2};

  span.sw { display: inline-block; width: 8px; height: 8px; border-radius: 2px; margin-right: 5px; }
  b { color: ${INK}; }
`;

const ReasonBlock = styled.div`
  border: 1px solid ${LINE};
  border-radius: 8px;
  margin-bottom: 7px;
  overflow: hidden;
`;

const ReasonHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 11px;
  background: ${props => props.bg};

  .title { flex: 1; font-size: 12px; font-weight: 700; color: ${props => props.fg}; }
  .pts { font-size: 10.5px; font-weight: 700; color: ${INK_3}; }
`;

const ReasonBody = styled.div`
  padding: 9px 11px;
  font-size: 12px;
  color: ${INK_2};
`;

const ReasonDetail = styled.div`
  line-height: 1.45;
  margin-bottom: 2px;
`;

const TaskList = styled.ol`
  margin: 0;
  padding: 0 0 0 18px;
  line-height: 1.5;
`;

const TaskButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.primaryColor};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.primaryColor};
    outline-offset: 2px;
    border-radius: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

const spin = keyframes`to { transform: rotate(360deg); }`;

const TaskSpinner = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-left: 6px;
  vertical-align: middle;
  border: 1.5px solid ${LINE};
  border-top-color: ${props => props.theme.primaryColor};
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const ReasonActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  &:not(:first-child) {
    margin-top: 8px;
  }
`;

const actionBtn = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
  text-decoration: none;
  font-family: inherit;
  cursor: pointer;
`;

const ReasonActionPrimary = styled(Link)`
  ${actionBtn}
  border: 1px solid ${props => props.theme.primaryColor};
  background: ${props => props.theme.primaryColor};
  color: white;

  &:hover { opacity: 0.92; color: white; }
`;

const ReasonActionSecondary = styled(Link)`
  ${actionBtn}
  border: 1px solid ${props => (props.muted ? LINE : props.theme.primaryColor)};
  background: white;
  color: ${props => (props.muted ? INK : props.theme.primaryColor)};

  &:hover {
    background: ${props => (props.muted ? BG : props.theme.primaryColor)};
    color: ${props => (props.muted ? INK : 'white')};
  }
`;

const ReasonActionButton = styled.button`
  ${actionBtn}
  border: 1px solid ${props => props.theme.primaryColor};
  background: ${props => props.theme.primaryColor};
  color: white;

  &:hover { opacity: 0.92; }
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px 16px;
`;

const MetaKv = styled.div`
  font-size: 11px;
  color: ${INK_3};
  b { display: block; font-size: 12.5px; color: ${INK}; font-weight: 700; margin-top: 1px; }
`;

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  padding: 11px 16px;
  border-top: 1px solid ${LINE_2};
`;

const footerBtn = `
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  min-height: 30px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  font-family: inherit;
`;

const FooterLink = styled(Link)`
  ${footerBtn}
  border: 1px solid ${LINE};
  background: white;
  color: ${INK};
  &:hover { background: ${BG}; }
`;

const FooterButton = styled.button`
  ${footerBtn}
  border: 1px solid ${props => (props.primary ? props.theme.primaryColor : LINE)};
  background: ${props => (props.primary ? props.theme.primaryColor : 'white')};
  color: ${props => (props.primary ? 'white' : INK)};
  opacity: ${props => (props.disabled ? 0.55 : 1)};
`;

class AttentionDetailPane extends React.Component {
  state = { maxPaneHeight: null, loadingTaskId: null };

  paneRef = React.createRef();

  componentDidMount() {
    this.mounted = true;
    this.observeScrollContainer();
    window.addEventListener('resize', this.updateMaxHeight);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.scrollContainerRef !== this.props.scrollContainerRef) {
      this.observeScrollContainer();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('resize', this.updateMaxHeight);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  getScrollEl = () => {
    const { scrollContainerRef } = this.props;
    return scrollContainerRef && scrollContainerRef.current ? scrollContainerRef.current : null;
  };

  observeScrollContainer = () => {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    const scrollEl = this.getScrollEl();
    if (!scrollEl) {
      this.updateMaxHeight();
      return;
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.updateMaxHeight);
      this.resizeObserver.observe(scrollEl);
    }
    this.updateMaxHeight();
  };

  // Bound the pane to the real viewport bottom (measured from where the pane
  // actually sits) rather than the shared container's fixed calc height, which
  // overshoots in the attention view and would crop the footer.
  updateMaxHeight = () => {
    const paneEl = this.paneRef.current;
    if (!paneEl) return;
    const { top } = paneEl.getBoundingClientRect();
    const nextHeight = Math.max(Math.floor(window.innerHeight - top - 16), 200);
    if (nextHeight !== this.state.maxPaneHeight) {
      this.setState({ maxPaneHeight: nextHeight });
    }
  };

  reasonKeys = () => {
    const { item, stripped } = this.props;
    const reason = item.reason_for_attention || {};
    const order = stripped ? ACTIONABLE_REASONS : REASON_ORDER;
    return order.filter(key => reason[key] && reason[key].length > 0);
  };

  scoreBreakdown = () => {
    const { item } = this.props;
    return (item && item.score_breakdown) || [];
  };

  pointsByKey = () => this.scoreBreakdown().reduce((acc, component) => {
    acc[component.key] = component.points;
    return acc;
  }, {});

  scoreSegments = () => {
    const { t } = this.props;
    return this.scoreBreakdown().map((component) => {
      const meta = REASON_META[component.key];
      if (component.key === UNREVIEWED_KEY) {
        return {
          key: component.key,
          label: t('assets.detail.newToQueue', { defaultValue: 'New to your queue' }),
          value: component.points,
          color: NEW_SEGMENT
        };
      }
      return {
        key: component.key,
        label: humanize(component.key),
        value: component.points,
        color: meta ? meta.segment : MUTED_BAR
      };
    });
  };

  openCreateTask = () => {
    const { item, taskActions: actions } = this.props;
    if (!item) return;
    actions.openTaskModal(
      null,
      {
        machine_id: item.id,
        machine_name: item.name,
        ara_id: item.ara_id,
        allowed_triggers: Object.keys(item.reason_for_attention || {}).filter(r => r !== 'tasks')
      }
    );
  };

  // Open a single task's detail modal in place (over the triage queue) rather
  // than navigating away, mirroring how the Activity list opens a task.
  openTaskDetails = (taskId) => {
    const { taskActions: actions, t } = this.props;
    if (!taskId || !actions) return;
    const prevLocation = {
      pathname: window.location.pathname,
      search: window.location.search
    };
    this.setState({ loadingTaskId: taskId });
    actions.getTaskDetails(taskId, false).then((taskDetails) => {
      actions.openTaskModal(taskId, taskDetails);
      window.history.replaceState(
        { prevLocation, modalOpen: true, from_app: true },
        '',
        `/tasks/${taskId}`
      );
      if (this.mounted) this.setState({ loadingTaskId: null });
    }).catch(() => {
      if (this.mounted) this.setState({ loadingTaskId: null });
      toastr.error(
        t('common.error', { defaultValue: 'Error' }),
        t('assets.detail.taskOpenFailed', { defaultValue: "Couldn't open task" })
      );
    });
  };

  reasonTitle = (key) => {
    const { item, t } = this.props;
    const meta = REASON_META[key];
    if (key === 'tasks') {
      const count = getReasonValues(item, 'tasks').length;
      return t('assets.detail.openTasksCount', { defaultValue: 'Open tasks ({{count}})', count });
    }
    const base = t(meta.titleKey, { defaultValue: meta.titleDefault });
    const value = getReasonValues(item, key)[0];
    if (!value) return base;
    const label = key === 'diagnostics_not_evaluable'
      ? t(`diagnosticRules.assignmentHealth.reasons.${value}`, { defaultValue: humanize(value) })
      : translateSharedAssetFilterValue(t, value);
    return label ? `${base} — ${label}` : base;
  };

  reasonDetail = (key) => {
    const { item, t } = this.props;
    const values = getReasonValues(item, key);
    const metadata = getReasonMetadata(item, key);

    switch (key) {
      case 'alarms': {
        const alarmMeta = metadata[0] || {};
        const parts = [
          alarmMeta.measurement_location || alarmMeta.component_name,
          alarmMeta.direction,
          alarmMeta.tag_id || alarmMeta.tag_name
        ].filter(Boolean);
        if (parts.length) return parts.join(' · ');
        return values[0] ? translateSharedAssetFilterValue(t, values[0]) : null;
      }
      case 'health_score':
        return values[0] ? translateSharedAssetFilterValue(t, values[0]) : null;
      case 'diagnostics': {
        const ruleMeta = metadata[0] || {};
        if (ruleMeta.rule_id) return t('assets.detail.ruleLabel', { defaultValue: 'Rule {{id}}', id: ruleMeta.rule_id });
        return values[0] ? humanize(String(values[0])) : null;
      }
      case 'diagnostics_not_evaluable':
        return values[0]
          ? t(`diagnosticRules.assignmentHealth.reasons.${values[0]}`, { defaultValue: humanize(values[0]) })
          : null;
      case 'baselines_status':
      case 'missing_asset_info':
        return values[0] ? translateSharedAssetFilterValue(t, values[0]) : null;
      case 'tasks':
        return null;
      default:
        return null;
    }
  };

  activityLink = (key) => {
    const { item } = this.props;
    const metadata = getReasonMetadata(item, key)[0] || {};
    const eventId = metadata.event_id;
    if (!eventId) return null;
    return {
      pathname: '/activity',
      state: { activity: { activity_id: eventId, type: 'event' } }
    };
  };

  renderReasonActions = (key) => {
    const { item, t } = this.props;
    const metadata = getReasonMetadata(item, key);

    switch (key) {
      case 'alarms': {
        const activityTo = this.activityLink(key);
        return (
          <ReasonActions>
            {activityTo && (
              <ReasonActionPrimary to={activityTo}>
                {t('assets.detail.actions.viewInActivity', { defaultValue: 'View in Activity' })}
              </ReasonActionPrimary>
            )}
            <ReasonActionSecondary to={`/machines/${item.id}/charts`}>
              {t('assets.detail.actions.viewChart', { defaultValue: 'View Chart' })}
            </ReasonActionSecondary>
          </ReasonActions>
        );
      }
      case 'health_score':
        return (
          <ReasonActions>
            <ReasonActionPrimary to={`/machines/${item.id}/health/health-score-details`}>
              {t('assets.detail.actions.viewHealthTrend', { defaultValue: 'View Health trend' })}
            </ReasonActionPrimary>
          </ReasonActions>
        );
      case 'diagnostics': {
        const ruleMeta = metadata[0] || {};
        const to = {
          pathname: `/machines/${item.id}/health`,
          ...(ruleMeta.rule_result_id ? { state: { result_id: ruleMeta.rule_result_id } } : {})
        };
        return (
          <ReasonActions>
            <ReasonActionPrimary to={to}>
              {t('assets.detail.actions.viewDiagnosis', { defaultValue: 'View Diagnosis' })}
            </ReasonActionPrimary>
          </ReasonActions>
        );
      }
      case 'baselines_status':
        return (
          <ReasonActions>
            <ReasonActionPrimary to={`/machines/${item.id}/baseline`}>
              {t('assets.detail.actions.configureBaselines', { defaultValue: 'Configure Baselines' })}
            </ReasonActionPrimary>
          </ReasonActions>
        );
      case 'missing_asset_info':
        return (
          <ReasonActions>
            <ReasonActionPrimary to={`/machines/${item.id}/info`}>
              {t('assets.detail.actions.completeSetup', { defaultValue: 'Complete Setup' })}
            </ReasonActionPrimary>
          </ReasonActions>
        );
      case 'diagnostics_not_evaluable':
        return (
          <ReasonActions>
            <ReasonActionPrimary to={`/machines/${item.id}/info`}>
              {t('assets.detail.actions.completeSetup', { defaultValue: 'Complete Setup' })}
            </ReasonActionPrimary>
          </ReasonActions>
        );
      case 'tasks':
        return (
          <ReasonActions>
            <ReasonActionSecondary muted to={`/machines/${item.id}/activity?filters=tasks&task_states=open,in_progress,scheduled,review_pending`}>
              {t('assets.detail.actions.viewTasks', { defaultValue: 'View Tasks' })}
            </ReasonActionSecondary>
            <ReasonActionButton type="button" onClick={this.openCreateTask}>
              {t('assets.detail.actions.createTask', { defaultValue: 'Create Task' })}
            </ReasonActionButton>
          </ReasonActions>
        );
      default:
        return null;
    }
  };

  renderReason = (key) => {
    const { item } = this.props;
    const meta = REASON_META[key];
    const pts = this.pointsByKey()[key];
    const headColors = {
      critical: { bg: '#fcefef', fg: CRITICAL },
      warning: { bg: '#fdf4e7', fg: '#a36006' },
      muted: { bg: BG, fg: INK }
    }[meta.tone];
    const detail = this.reasonDetail(key);
    const values = getReasonValues(item, key);
    const metadata = getReasonMetadata(item, key);

    return (
      <ReasonBlock key={key}>
        <ReasonHead bg={headColors.bg} fg={headColors.fg}>
          <span aria-hidden="true">{meta.glyph}</span>
          <span className="title">{this.reasonTitle(key)}</span>
          {pts != null && <span className="pts">{`+${pts}`}</span>}
        </ReasonHead>
        <ReasonBody>
          {key === 'tasks' && values.length > 0 && (
            <TaskList>
              {values.map((value, i) => {
                const label = formatTaskLabel(value, metadata[i]);
                const clickId = getTaskClickId(value);
                const loading = clickId != null && this.state.loadingTaskId === clickId;
                return (
                  <li key={getTaskId(value) || i}>
                    {clickId != null ? (
                      <TaskButton
                        type="button"
                        disabled={loading}
                        onClick={() => this.openTaskDetails(clickId)}
                      >
                        {label}
                        {loading && <TaskSpinner aria-hidden="true" />}
                      </TaskButton>
                    ) : label}
                  </li>
                );
              })}
            </TaskList>
          )}
          {detail && <ReasonDetail>{detail}</ReasonDetail>}
          {this.renderReasonActions(key)}
        </ReasonBody>
      </ReasonBlock>
    );
  };

  render() {
    const {
      item, onClose, onToggleReviewed, stripped, t
    } = this.props;
    if (!item) return null;

    const score = Number(item.urgency_score) || 0;
    const bucket = getUrgencyBucket(score);
    const scoreTone = SCORE_TONES[bucket.key];
    const condition = CONDITION_META[item.condition];
    const conditionLabel = condition
      ? t(`machineCondition.${condition.value}`, { defaultValue: condition.text })
      : null;
    const reviewed = !!item.attention_machine_viewed;
    const segments = this.scoreSegments();
    const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
    const progress = Math.max(0, Math.min(100, Number(item.setup_completion_percent) || 0));

    return (
      <Pane
        innerRef={(node) => { this.paneRef.current = node; }}
        style={this.state.maxPaneHeight ? { maxHeight: this.state.maxPaneHeight } : undefined}
      >
        <Header>
          <HeaderTop>
            <Dot
              color={condition ? condition.color : null}
              role="img"
              aria-label={conditionLabel}
              title={conditionLabel}
            />
            <Name>{item.name}</Name>
            {!stripped && <ScoreChip bg={scoreTone.bg} fg={scoreTone.fg}>{score}</ScoreChip>}
            <CloseBtn type="button" onClick={onClose} aria-label={t('common.close', { defaultValue: 'Close' })}>×</CloseBtn>
          </HeaderTop>
          <Area>{item.area_path || item.area_name || '--'}</Area>
        </Header>

        <Body>
          {!stripped && segments.length > 0 && (
            <Section>
              <SectionTitle>
                {t('assets.detail.whyScore', { defaultValue: 'Why score {{score}}', score })}
              </SectionTitle>
              <BreakBar>
                {segments.map(seg => (
                  <i key={seg.key} style={{ width: `${(seg.value / total) * 100}%`, background: seg.color }} title={`${seg.label}: +${seg.value}`} />
                ))}
              </BreakBar>
              <Legend>
                {segments.map(seg => (
                  <span key={seg.key}>
                    <span className="sw" style={{ background: seg.color }} />
                    {seg.label}
                    {' '}
                    <b>{`+${seg.value}`}</b>
                  </span>
                ))}
              </Legend>
            </Section>
          )}

          <Section>
            <SectionTitle>{t('assets.detail.reasons', { defaultValue: 'Reasons & actions' })}</SectionTitle>
            {this.reasonKeys().map(key => this.renderReason(key))}
          </Section>

          {!stripped && (
            <Section>
              <ContextualTrend item={item} />
            </Section>
          )}

          <Section>
            <SectionTitle>{t('assets.detail.asset', { defaultValue: 'Asset' })}</SectionTitle>
            <MetaGrid>
              <MetaKv>
                {t('assets.headers.state', { defaultValue: 'State' })}
                <b>{MACHINE_STATE[item.current_state] || 'NA'}</b>
              </MetaKv>
              <MetaKv>
                {t('assets.headers.lastMeasurement', { defaultValue: 'Last Measurement' })}
                <b>{item.last_measurement_time ? localTimeString(item.last_measurement_time) : '--'}</b>
              </MetaKv>
              <MetaKv>
                {t('assets.headers.conditionSince', { defaultValue: 'Condition since' })}
                <b>{item.condition_since ? getRelativeDuration(item.condition_since) : '--'}</b>
              </MetaKv>
              <MetaKv>
                {t('assets.triage.setup', { defaultValue: 'Setup' })}
                <b>{`${progress}%`}</b>
              </MetaKv>
            </MetaGrid>
          </Section>
        </Body>

        <Footer>
          <FooterButton primary type="button" onClick={this.openCreateTask}>
            {t('assets.detail.actions.createTask', { defaultValue: 'Create Task' })}
          </FooterButton>
          <FooterButton
            type="button"
            onClick={() => onToggleReviewed(item.id, reviewed)}
            title={reviewed
              ? t('assets.triage.markUnreviewed', { defaultValue: 'Mark unreviewed' })
              : t('assets.triage.markReviewed', { defaultValue: 'Mark Reviewed' })}
          >
            {reviewed
              ? t('assets.triage.reviewedUndo', { defaultValue: 'Reviewed ✓ · Undo' })
              : t('assets.triage.markReviewed', { defaultValue: 'Mark Reviewed' })}
          </FooterButton>
          <FooterLink to={`/machines/${item.id}`}>
            {t('assets.detail.openAssetPage', { defaultValue: 'Open Asset Page' })}
          </FooterLink>
        </Footer>
      </Pane>
    );
  }
}

AttentionDetailPane.propTypes = {
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onToggleReviewed: PropTypes.func.isRequired,
  stripped: PropTypes.bool,
  scrollContainerRef: PropTypes.shape({ current: PropTypes.object }),
  taskActions: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired
};

AttentionDetailPane.defaultProps = {
  item: null,
  stripped: false,
  scrollContainerRef: null
};

const mapDispatchToProps = dispatch => ({
  taskActions: bindActionCreators(taskActions, dispatch)
});

export default connect(null, mapDispatchToProps)(withTranslation()(AttentionDetailPane));
